import styled from 'styled-components';

import Button from 'components/shared/Button';
import { simpleDateAtTime } from 'lib/date';
import { Body, BodySmall, LabelBold } from 'styles/typography';

import { useManualLaunch } from './ManualLaunchProvider';
import { OperationalTaskCard, PatientTaskCard, TaskCard as TaskCardModel } from './taskCards';

interface Props {
  card: TaskCardModel;
  // The portal's own name, for the launch button's label.
  portalName: string;
  // Whether `Open in <portal>` may render at all — mode is manual or extension AND the
  // extension answered the presence probe. The card never decides this itself: the mode matrix
  // is the page's business and the probe is one page-level answer, not a per-card coin flip.
  canOpenInPortal: boolean;
  // The probe hasn't answered yet. Distinct from `canOpenInPortal: false`, which means it
  // answered NO: the card must not open the M1/M2 gate on a question still in flight.
  detectingExtension: boolean;
  onOpen: (workflowId: string) => void;
  onManagePortals: (sourceSystem?: string) => void;
}

export default function TaskCard({
  card,
  portalName,
  canOpenInPortal,
  detectingExtension,
  onOpen,
  onManagePortals,
}: Props) {
  if (card.kind === 'operational') return <OperationalCard card={card} onManagePortals={onManagePortals} />;
  return (
    <PatientCard
      card={card}
      portalName={portalName}
      canOpenInPortal={canOpenInPortal}
      detectingExtension={detectingExtension}
      onOpen={onOpen}
    />
  );
}

function PatientCard({
  card,
  portalName,
  canOpenInPortal,
  detectingExtension,
  onOpen,
}: {
  card: PatientTaskCard;
  portalName: string;
  canOpenInPortal: boolean;
  detectingExtension: boolean;
  onOpen: (workflowId: string) => void;
}) {
  const { activeWorkflowId, launchingWorkflowId, unlockedWorkflowIds, launch } = useManualLaunch();

  const showLaunch = canOpenInPortal && !!card.launch;
  const unlocked = unlockedWorkflowIds.has(card.workflowId);
  // The M1/M2 gate: until the portal tab has been opened for this task there is nothing to
  // review, so the name is text rather than a link.
  //
  // Detection counts as gated. Treating "not yet known" as "no extension" would publish the
  // M0 surface for the second or so the probe takes, so the name would be a link and then
  // stop being one the moment the extension answered — the card rearranging itself under
  // whoever is reading it, and the gate briefly open.
  const openable = !(showLaunch || (detectingExtension && !!card.launch)) || unlocked;
  const isOpen = activeWorkflowId === card.workflowId;
  // Single-launch arbitration in the UI. The extension permits exactly one manual job
  // browser-wide and answers a second with an error; disabling the other buttons is the same
  // rule stated before the click rather than after it.
  const blockedByOtherLaunch = activeWorkflowId !== null && !isOpen;

  return (
    <Card data-testid='task-card' data-kind='patient'>
      <Header>
        <TitleRow>
          {openable ? (
            <TitleLink type='button' onClick={() => onOpen(card.workflowId)} data-testid='task-card-open'>
              {card.title}
            </TitleLink>
          ) : (
            <Title data-testid='task-card-title'>{card.title}</Title>
          )}
          {card.badge && <SourceBadge data-testid='task-card-badge'>{card.badge}</SourceBadge>}
        </TitleRow>
        {card.createdAt && <Created>Created {simpleDateAtTime(card.createdAt)}</Created>}
      </Header>
      <Body>{card.body}</Body>
      {showLaunch && (
        <Actions>
          <Button
            size='small'
            variant={openable ? 'outline-primary' : 'primary'}
            loading={launchingWorkflowId === card.workflowId}
            disabled={isOpen || blockedByOtherLaunch}
            data-testid='task-card-launch'
            onClick={() =>
              launch({
                workflowId: card.workflowId,
                actionId: card.launch!.actionId,
                entryUrl: card.launch!.entryUrl,
                sourceSystem: card.sourceSystem,
                portalPatientName: card.portalPatientName,
              })
            }>
            {isOpen ? `Opened in ${portalName}` : `Open in ${portalName}`}
          </Button>
        </Actions>
      )}
    </Card>
  );
}

function OperationalCard({
  card,
  onManagePortals,
}: {
  card: OperationalTaskCard;
  onManagePortals: (sourceSystem?: string) => void;
}) {
  return (
    <Card $operational data-testid='task-card' data-kind='operational'>
      <Header>
        <Title data-testid='task-card-title'>Action needed: {card.title}</Title>
      </Header>
      <Body>{card.body}</Body>
      <Actions>
        {/* Both operational cards route here — and the rejected-password one deliberately
            offers no retry: retrying a rejected password is how an account gets locked out. */}
        <Button size='small' onClick={() => onManagePortals(card.sourceSystem)} data-testid='task-card-manage-portals'>
          Manage Connected Portals
        </Button>
      </Actions>
    </Card>
  );
}

const Card = styled.div<{ $operational?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme, $operational }) => ($operational ? theme.colors.yellow100 : theme.colors.black10)};
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled(LabelBold)`
  font-size: 16px;
`;

const TitleLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
`;

const SourceBadge = styled(BodySmall)`
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.black10};
  color: ${({ theme }) => theme.colors.black75};
`;

const Created = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;
